import { Dialog } from "@headlessui/react";
import { Fragment, useState, useRef, useEffect, useCallback } from "react";
import { EllipsisVerticalIcon } from "@heroicons/react/24/outline";
import { createPortal } from "react-dom";
import { DueReminder } from "../../types/reminder";
import Button from "../ui/Button";

interface DueRemindersModalProps {
  isOpen: boolean;
  onClose: () => void;
  reminders: DueReminder[];
  onDismiss: (id: number) => void;
  onGoToPitch: (pitchId: number, id: number) => void;
  onEdit: (reminder: DueReminder) => void;
  onDelete: (id: number) => void;
}

const formatRelativeDate = (iso: string): string => {
  try {
    const reminderDate = new Date(iso);
    if (Number.isNaN(reminderDate.getTime())) return iso;

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    reminderDate.setHours(0, 0, 0, 0);

    const diffMs = today.getTime() - reminderDate.getTime();
    const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return "Today";
    if (diffDays === 1) return "1 day ago";
    if (diffDays > 1) return `${diffDays} days ago`;
    if (diffDays === -1) return "Tomorrow";
    return `In ${Math.abs(diffDays)} days`;
  } catch {
    return iso;
  }
};

const isDueWithin3Days = (iso: string): boolean => {
  try {
    const reminderDate = new Date(iso);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    reminderDate.setHours(0, 0, 0, 0);
    const diffDays = Math.round(
      (reminderDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24)
    );
    return diffDays <= 3;
  } catch {
    return false;
  }
};

interface DropdownMenuProps {
  reminder: DueReminder;
  onEdit: (r: DueReminder) => void;
  onView: (pitchId: number, id: number) => void;
  onDismiss: (id: number) => void;
  onDeleteClick: (id: number) => void;
}

function DropdownMenu({ reminder: r, onEdit, onView, onDismiss, onDeleteClick }: DropdownMenuProps) {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0 });
  const btnRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const handleToggle = () => {
    if (!open && btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      setCoords({ top: rect.bottom + 4, left: rect.right - 160 });
    }
    setOpen((v) => !v);
  };

  const handleClickOutside = useCallback((e: MouseEvent) => {
    if (
      menuRef.current && !menuRef.current.contains(e.target as Node) &&
      btnRef.current && !btnRef.current.contains(e.target as Node)
    ) {
      setOpen(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open, handleClickOutside]);

  return (
    <>
      <button
        ref={btnRef}
        onClick={handleToggle}
        className="p-1.5 rounded-md hover:bg-neutral-100 transition-colors"
      >
        <EllipsisVerticalIcon className="w-5 h-5 text-neutral-500" />
      </button>

      {open && createPortal(
        <div
          ref={menuRef}
          style={{ position: "fixed", top: coords.top, left: coords.left, zIndex: 9999 }}
          className="w-40 bg-white rounded-md shadow-lg ring-1 ring-black ring-opacity-5 divide-y divide-neutral-100"
        >
          <div className="py-1">
            <button
              onClick={() => { setOpen(false); onEdit(r); }}
              className="flex w-full items-center px-4 py-2 text-sm text-primary-700 hover:bg-neutral-50"
            >
              Edit
            </button>
            <button
              onClick={() => { setOpen(false); onView(r.pitchId, r.id); }}
              className="flex w-full items-center px-4 py-2 text-sm text-primary-700 hover:bg-neutral-50"
            >
              View
            </button>
            <button
              onClick={() => { setOpen(false); onDismiss(r.id); }}
              className="flex w-full items-center px-4 py-2 text-sm text-neutral-600 hover:bg-neutral-50"
            >
              Dismiss
            </button>
          </div>
          <div className="py-1">
            <button
              onClick={() => { setOpen(false); onDeleteClick(r.id); }}
              className="flex w-full items-center px-4 py-2 text-sm text-danger-600 hover:bg-neutral-50"
            >
              Delete
            </button>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}

export default function DueRemindersModal({
  isOpen,
  onClose,
  reminders,
  onDismiss,
  onGoToPitch,
  onEdit,
  onDelete,
}: DueRemindersModalProps) {
  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null);

  const handleDeleteConfirm = () => {
    if (confirmDeleteId !== null) {
      onDelete(confirmDeleteId);
      setConfirmDeleteId(null);
    }
  };

  return (
    <>
      <Dialog
        open={isOpen}
        onClose={onClose}
        className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
      >
        <Dialog.Panel
          className="bg-white rounded-lg p-6 w-full max-w-3xl max-h-[90vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
        >
          <Dialog.Title className="text-lg font-semibold mb-1">
            📋 {reminders.length} Pitch Reminders Due 
          </Dialog.Title>
          <p className="text-sm text-neutral-500 mb-4">
            The following pitches have reminders due today or past due.
          </p>

          <div className="overflow-auto border border-neutral-200 rounded-lg max-h-[60vh]">
            <table className="min-w-full divide-y divide-neutral-200 ">
              <thead className="bg-neutral-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-neutral-500 uppercase tracking-wide">
                    Artist Name
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-neutral-500 uppercase tracking-wide">
                    Release Name
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-neutral-500 uppercase tracking-wide">
                    DSP
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-neutral-500 uppercase tracking-wide">
                    Due Date
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-neutral-500 uppercase tracking-wide">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-200 bg-white">
                {reminders.map((r) => {
                  const overdue = isDueWithin3Days(r.remindAt);
                  return (
                    <tr key={r.id} className="hover:bg-neutral-50 transition-colors">
                      <td className="px-4 py-3 text-sm font-medium text-black whitespace-nowrap">
                        {r.pitch?.artistName || "—"}
                      </td>
                      <td className="px-4 py-3 text-sm text-neutral-700 whitespace-nowrap">
                        {r.pitch?.releaseTitle || "—"}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        {r.dsp ? (
                          <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium text-warning-700 bg-warning-100 rounded">
                            {r.dsp}
                          </span>
                        ) : (
                          <span className="text-sm text-neutral-400">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className={`text-sm font-medium ${overdue ? "text-danger-600" : "text-neutral-700"}`}>
                          {formatRelativeDate(r.remindAt)}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-right">
                        <DropdownMenu
                          reminder={r}
                          onEdit={onEdit}
                          onView={onGoToPitch}
                          onDismiss={onDismiss}
                          onDeleteClick={setConfirmDeleteId}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="flex justify-end mt-6">
            <Button
              variant="neutral"
              onClick={onClose}
            >
              Close
            </Button>
          </div>
        </Dialog.Panel>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog
        open={confirmDeleteId !== null}
        onClose={() => setConfirmDeleteId(null)}
        className="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-50"
      >
        <Dialog.Panel className="bg-white rounded-lg p-6 w-full max-w-sm">
          <Dialog.Title className="text-base font-semibold text-black mb-2">
            Delete Reminder
          </Dialog.Title>
          <p className="text-sm text-neutral-600 mb-6">
            Are you sure you want to delete this reminder? This action cannot be undone.
          </p>
          <div className="flex justify-end gap-3">
            <Button
              variant="neutral"
              onClick={() => setConfirmDeleteId(null)}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={handleDeleteConfirm}
            >
              Delete
            </Button>
          </div>
        </Dialog.Panel>
      </Dialog>
    </>
  );
}
